import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

/**
 * On-demand candidate detail for one bug (QA-7428) — the only place descriptions are fetched
 * from, since the main list endpoint deliberately omits them to stay lightweight. Pass a falsy
 * `key` to skip fetching (dialog closed); the effect only re-runs when `key` itself changes, so
 * the dialog staying open does not refetch on every render.
 */
export default function useDuplicateCandidates(key) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCandidates = useCallback(async (isCancelled = () => false) => {
    if (!key) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${QUALITY_SERVICE_API_URL}/hub/duplicates/${encodeURIComponent(key)}`);
      if (!isCancelled()) setData(response.data);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    let cancelled = false;
    fetchCandidates(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchCandidates]);

  return { data, loading, error, refetch: () => fetchCandidates() };
}
